import { DateFormat } from "common/helpers/Format";
import { titleCaseString } from "common/helpers/String";
import { format, parseISO } from "date-fns";
import { List, Record } from "immutable";

export const statsFromJSON = (json: Partial<IStatsData>): StatsRecord => {
  const data: IStatsRecord = { ...(json as any) };
  return new StatsRecord(data);
};

const colors: { [key in keyof IStatsRecord]: string } = {
  jobs: "#1E77B4",
  estimates: "#AEC7E8",
  appointments: "#FF7F0E",
  screen_shares: "#FFBB78",
  documents: "#2CA02C",
  images: "#98DF8A",
  visualizations: "#D62728",
  chameleon: "#1E77B4",
  chameleon_v2: "#AEC7E8",
  renoworks: "#FF7F0E",
  hover: "#FFBB78",
  signatures: "#8C564B",
  reports: "#E377C2",
  skymeasure_roof: "#1E77B4",
  skymeasure_walls: "#AEC7E8",
  eagleview_roofing: "#98DF8A",
  estimates_amount: "#1E77B4",
  signatures_amount: "#AEC7E8",
  quickmeasure_roofing: "#D62628",
  roof_scope: "#D62728",
  plnar: "#9467BD",
  month: "", // Not an actual stat
};

export const activityStatsProps = [
  "jobs",
  "estimates",
  "appointments",
  "screen_shares",
  "documents",
  "images",
  "visualizations",
  "reports",
  "signatures",
] as (keyof IStatsRecord)[];

export const visualizationStatsProps = ["chameleon", "chameleon_v2", "renoworks", "hover"] as (keyof IStatsRecord)[];

export const reportsStatsProps = [
  "skymeasure_roof",
  "skymeasure_walls",
  "eagleview_roofing",
  "quickmeasure_roofing",
] as (keyof IStatsRecord)[];

export const dollarActivityStatsProps = ["estimates_amount", "signatures_amount"] as (keyof IStatsRecord)[];

export const statsTableData = (statsList: List<StatsRecord> | undefined, props: (keyof IStatsRecord)[]) => {
  if (!statsList || statsList.isEmpty()) {
    return {};
  }

  const obj = {} as any;
  props.forEach((prop) => {
    obj[prop] = 0;
    statsList.forEach((s) => {
      const value = s.get(prop) || 0;
      const numberValue = typeof value === "string" ? parseFloat(value) : value;
      obj[prop] += numberValue;
    });
  });
  return obj;
};

export const statsChartData = (statsList: List<StatsRecord> | undefined, props: (keyof IStatsRecord)[]) => {
  if (!statsList || statsList.isEmpty()) {
    return {
      labels: [],
      datasets: [],
    };
  }

  statsList = statsList.sortBy((s) => new Date(s.month).getTime());

  const labels = statsList.map((s) => format(parseISO(s.month), "MMM yyyy"));

  return {
    labels: labels.toArray(),
    datasets: props.map((prop) => ({
      label: titleCaseString(prop),
      data: statsList.map((s) => s.get(prop) || 0).toArray() || [],
      backgroundColor: colors[prop],
      borderColor: colors[prop],
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: colors[prop],
      pointHoverBorderColor: colors[prop],
    })),
  };
};

export interface IStatsData {
  month: string;
  jobs: number;
  estimates: number;
  appointments: number;
  screen_shares: number;
  documents: number;
  images: number;
  visualizations: number;
  chameleon: number;
  chameleon_v2: number;
  renoworks: number;
  hover: number;
  signatures: number;
  reports: number;
  skymeasure_roof: number;
  skymeasure_walls: number;
  eagleview_roofing: number;
  estimates_amount: string;
  signatures_amount: string;
  quickmeasure_roofing: number;
  roof_scope: number;
  plnar: number;
}

export interface IStatsRecord {
  month: string;
  jobs: number;
  estimates: number;
  appointments: number;
  screen_shares: number;
  documents: number;
  images: number;
  visualizations: number;
  chameleon: number;
  chameleon_v2: number;
  renoworks: number;
  hover: number;
  signatures: number;
  reports: number;
  skymeasure_roof: number;
  skymeasure_walls: number;
  eagleview_roofing: number;
  estimates_amount: string;
  signatures_amount: string;
  quickmeasure_roofing: number;
  roof_scope: number;
  plnar: number;
}

const defaultStatsProps: IStatsRecord = {
  month: "",
  jobs: 0,
  estimates: 0,
  appointments: 0,
  screen_shares: 0,
  documents: 0,
  images: 0,
  visualizations: 0,
  chameleon: 0,
  chameleon_v2: 0,
  renoworks: 0,
  hover: 0,
  signatures: 0,
  reports: 0,
  skymeasure_roof: 0,
  skymeasure_walls: 0,
  eagleview_roofing: 0,
  estimates_amount: "0",
  signatures_amount: "0",
  quickmeasure_roofing: 0,
  roof_scope: 0,
  plnar: 0,
};

export class StatsRecord extends Record(defaultStatsProps) implements IStatsRecord {}
